import * as Yup from "yup";
import type { KeyLocationCatalogItem } from "../services/propertyKeyLocation";
import getValidationErrors, { type Errors } from "./getValidationError";
import {
  validateKeyLocationNoteForOthers,
  validateKeyLocationPasswordForDigital,
} from "./keyLocationRules";
import { yupPropertyAreaString } from "./propertyArea";
import { optionalAlternativeContactYupFields, validatePropertyAlternativeContact } from "./propertyAlternativeContact";
import {
  FURNITURE_STATUS_INVALID_MESSAGE,
  FURNITURE_STATUS_REQUIRED_MESSAGE,
  FURNITURE_STATUS_VALUES,
} from "./propertyFurniture";
import {
  commissionFormFromApi,
  firstCommissionError,
  validatePropertyCommission,
  type CommissionType,
} from "./propertyCommission";

export type AddressStepData = {
  logradouro: string;
  cep: string;
  number: string;
  neighborhood: string;
  city: string;
  uf: string;
  complement?: string;
  floor?: string;
};

export type DescriptionStepData = {
  property_type_id: number | null | undefined;
  zap_publication_type: string | null | undefined;
  owner_email: string;
  property_consultant: string;
  title: string;
  description: string;
  rooms: number | null | undefined;
  suites: number | null | undefined;
  bathrooms: number | null | undefined;
  garage: number | null | undefined;
  rent_value: string;
  condominium_value: string;
  iptu_value: string;
  area: string;
  is_funished: number | null | undefined;
  allow_pets: number | null | undefined;
  water_included: number | null | undefined;
  gas_included: number | null | undefined;
  guarantees: number[];
  condominium?: string;
  key_location_id?: number | null;
  key_location_note?: string | null;
  key_location_password?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  commission_type?: CommissionType | string | null;
  admin_percentage?: string | number | null;
  first_rent_commission_7cantos_percentage?: string | number | null;
  partner_name?: string | null;
  partner_first_rent_commission_percentage?: string | number | null;
};

export type CharacteristicsStepData = {
  rooms?: number | null;
  suites?: number | null;
  bathrooms?: number | null;
  garage?: number | null;
  is_funished?: number | null;
  allow_pets?: number | null;
  water_included?: number | null;
  gas_included?: number | null;
  condominium?: string;
  is_nearby_clinics: number | null | undefined;
  is_nearby_major_roads: number | null | undefined;
  is_nearby_schools: number | null | undefined;
  is_nearby_mall: number | null | undefined;
  is_nearby_public_transport: number | null | undefined;
  has_24h_concierge: number | null | undefined;
  has_ac: number | null | undefined;
  has_backyard: number | null | undefined;
  has_bbq: number | null | undefined;
  has_cable_tv: number | null | undefined;
  has_elevator: number | null | undefined;
  has_game_room: number | null | undefined;
  has_garden: number | null | undefined;
  has_gourmet: number | null | undefined;
  has_gourmet_porch: number | null | undefined;
  has_gym: number | null | undefined;
  has_home_cinema: number | null | undefined;
  has_party: number | null | undefined;
  has_playground: number | null | undefined;
  has_pool: number | null | undefined;
  has_porch: number | null | undefined;
  has_sauna: number | null | undefined;
  has_sea_view: number | null | undefined;
  has_spa: number | null | undefined;
  has_sports: number | null | undefined;
  has_storage: number | null | undefined;
  has_tennis_court: number | null | undefined;
};

export type StepValidationResult =
  | { ok: true }
  | { ok: false; errors: Errors; firstMessage: string };

const CEP_REGEX = /^\d{5}-\d{3}$/;

const addressSchema = Yup.object().shape({
  logradouro: Yup.string().required("Logradouro obrigatório"),
  cep: Yup.string()
    .required("CEP obrigatório")
    .matches(CEP_REGEX, "CEP inválido"),
  number: Yup.string().required("Número obrigatório"),
  neighborhood: Yup.string().required("Bairro obrigatório"),
  city: Yup.string().required("Cidade obrigatória"),
  uf: Yup.string().required("UF obrigatório"),
});

const descriptionSchema = Yup.object().shape({
  property_type_id: Yup.number().required("Informe o tipo do imóvel"),
  zap_publication_type: Yup.string().required(
    "Informe o tipo do publicação no ZAP"
  ),
  owner_email: Yup.string().required("E-mail do proprietário obrigatório"),
  property_consultant: Yup.string().required("Consultante obrigatório"),
  title: Yup.string().required("Título obrigatório"),
  description: Yup.string().required("Descrição obrigatória"),
  rooms: Yup.number()
    .required("Número de quartos obrigatório")
    .min(1, "Deve conter no mínimo 1 quarto"),
  suites: Yup.number()
    .required("Número de suítes obrigatório")
    .max(5, "Número máximo de suítes atingido"),
  bathrooms: Yup.number()
    .required("Número de banheiros obrigatório")
    .max(10, "Número máximo de banheiros atingido"),
  garage: Yup.number()
    .required("Número de garagens obrigatório")
    .max(5, "Número máximo de vagas atingido"),
  rent_value: Yup.string().required("Valor do aluguel obrigatório"),
  condominium_value: Yup.string().required("Valor do condomínio obrigatório"),
  iptu_value: Yup.string().required("Valor do iptu obrigatório"),
  area: yupPropertyAreaString(),
  is_funished: Yup.number()
    .oneOf([...FURNITURE_STATUS_VALUES], FURNITURE_STATUS_INVALID_MESSAGE)
    .required(FURNITURE_STATUS_REQUIRED_MESSAGE),
  allow_pets: Yup.number().required("Informe se o imóvel permite pets"),
  water_included: Yup.number().required(
    "Informe se o imóvel tem água inclusa"
  ),
  gas_included: Yup.number().required("Informe se o imóvel tem gás inclusa"),
  guarantees: Yup.array()
    .required("Mínimo de 1 garantia")
    .min(1, "Necessária ao menos 1 garantia"),
  condominium: Yup.string(),
  key_location_id: Yup.number().nullable(),
  key_location_note: Yup.string().max(255).nullable(),
  ...optionalAlternativeContactYupFields(),
});

const propertyDataSchema = descriptionSchema.pick([
  "property_type_id",
  "zap_publication_type",
  "rent_value",
  "condominium_value",
  "iptu_value",
  "area",
  "guarantees",
]);

const publicationSchema = descriptionSchema.pick([
  "owner_email",
  "property_consultant",
  "title",
  "description",
  "key_location_id",
  "key_location_note",
]);

const PUBLICATION_ERROR_KEYS = new Set([
  "owner_email",
  "property_consultant",
  "title",
  "description",
  "key_location_note",
  "key_location_password",
  "contact_email",
  "contact_phone",
]);

const CHARACTERISTIC_ERROR_KEYS = new Set([
  "rooms",
  "suites",
  "bathrooms",
  "garage",
  "garages",
  "is_funished",
  "is_furnished",
  "allow_pets",
  "water_included",
  "gas_included",
]);

export function stepForDescriptionErrors(
  errors: Errors
): "description" | "publication" | "characteristic" {
  const keys = Object.keys(errors);
  if (keys.length === 0) return "description";
  if (keys.every((key) => PUBLICATION_ERROR_KEYS.has(key))) {
    return "publication";
  }
  if (keys.every((key) => CHARACTERISTIC_ERROR_KEYS.has(key))) {
    return "characteristic";
  }
  if (keys.some((key) => PUBLICATION_ERROR_KEYS.has(key))) {
    return "publication";
  }
  if (keys.some((key) => CHARACTERISTIC_ERROR_KEYS.has(key))) {
    return "characteristic";
  }
  return "description";
}

const requiredFlag = Yup.number().required("Informe o valor dessa campo");

const characteristicsSchema = Yup.object().shape({
  rooms: Yup.number()
    .required("Número de quartos obrigatório")
    .min(1, "Deve conter no mínimo 1 quarto"),
  suites: Yup.number()
    .required("Número de suítes obrigatório")
    .max(5, "Número máximo de suítes atingido"),
  bathrooms: Yup.number()
    .required("Número de banheiros obrigatório")
    .max(10, "Número máximo de banheiros atingido"),
  garage: Yup.number()
    .required("Número de garagens obrigatório")
    .max(5, "Número máximo de vagas atingido"),
  is_funished: Yup.number()
    .oneOf([...FURNITURE_STATUS_VALUES], FURNITURE_STATUS_INVALID_MESSAGE)
    .required(FURNITURE_STATUS_REQUIRED_MESSAGE),
  allow_pets: Yup.number().required("Informe se o imóvel permite pets"),
  water_included: Yup.number().required(
    "Informe se o imóvel tem água inclusa"
  ),
  gas_included: Yup.number().required("Informe se o imóvel tem gás inclusa"),
  condominium: Yup.string(),
  is_nearby_clinics: requiredFlag,
  is_nearby_major_roads: requiredFlag,
  is_nearby_schools: requiredFlag,
  is_nearby_mall: requiredFlag,
  is_nearby_public_transport: requiredFlag,
  has_24h_concierge: requiredFlag,
  has_ac: requiredFlag,
  has_backyard: requiredFlag,
  has_bbq: requiredFlag,
  has_cable_tv: requiredFlag,
  has_elevator: requiredFlag,
  has_game_room: requiredFlag,
  has_garden: requiredFlag,
  has_gourmet: requiredFlag,
  has_gourmet_porch: requiredFlag,
  has_gym: requiredFlag,
  has_home_cinema: requiredFlag,
  has_party: requiredFlag,
  has_playground: requiredFlag,
  has_pool: requiredFlag,
  has_porch: requiredFlag,
  has_sauna: requiredFlag,
  has_sea_view: requiredFlag,
  has_spa: requiredFlag,
  has_sports: requiredFlag,
  has_storage: requiredFlag,
  has_tennis_court: requiredFlag,
});

function toResult(err: Yup.ValidationError): StepValidationResult {
  const errors = getValidationErrors(err);
  const firstKey = Object.keys(errors)[0];
  return {
    ok: false,
    errors,
    firstMessage: firstKey ? errors[firstKey] : err.message,
  };
}

function normalizeRequiredText(value: unknown): string {
  if (value == null) return "";
  const text = String(value).trim();
  if (text === "" || text === "null" || text === "undefined") return "";
  return text;
}

function normalizeMoneyForValidation(value: unknown): string {
  return normalizeRequiredText(String(value ?? "").replaceAll("R$", ""));
}

function normalizeAreaForValidation(value: unknown): string {
  return normalizeRequiredText(String(value ?? "").replaceAll("m²", ""));
}

export function validateAddressStep(data: AddressStepData): StepValidationResult {
  try {
    addressSchema.validateSync(data, { abortEarly: false });
    return { ok: true };
  } catch (err) {
    if (err instanceof Yup.ValidationError) return toResult(err);
    throw err;
  }
}

function validateCommissionFields(
  data: DescriptionStepData
): StepValidationResult | null {
  const commissionErrors = validatePropertyCommission(
    commissionFormFromApi({
      commission_type: data.commission_type,
      admin_percentage: data.admin_percentage,
      first_rent_commission_7cantos_percentage:
        data.first_rent_commission_7cantos_percentage,
      partner_name: data.partner_name,
      partner_first_rent_commission_percentage:
        data.partner_first_rent_commission_percentage,
    })
  );
  if (Object.keys(commissionErrors).length === 0) return null;
  return {
    ok: false,
    errors: commissionErrors,
    firstMessage: firstCommissionError(commissionErrors),
  };
}

export function validatePropertyDataStep(
  data: DescriptionStepData
): StepValidationResult {
  const commissionResult = validateCommissionFields(data);
  if (commissionResult) return commissionResult;

  try {
    propertyDataSchema.validateSync(
      {
        ...data,
        zap_publication_type: normalizeRequiredText(data.zap_publication_type),
        rent_value: normalizeMoneyForValidation(data.rent_value),
        condominium_value: normalizeMoneyForValidation(data.condominium_value),
        iptu_value: normalizeMoneyForValidation(data.iptu_value),
        area: normalizeAreaForValidation(data.area),
      },
      { abortEarly: false }
    );
    return { ok: true };
  } catch (err) {
    if (err instanceof Yup.ValidationError) return toResult(err);
    throw err;
  }
}

export type KeyLocationStepOptions = {
  allowBlankDigitalPassword?: boolean;
};

function validateKeyLocationStepFields(
  data: DescriptionStepData,
  keyLocationCatalog: KeyLocationCatalogItem[],
  options: KeyLocationStepOptions = {}
): StepValidationResult | null {
  const othersNoteErr = validateKeyLocationNoteForOthers(
    keyLocationCatalog,
    data.key_location_id ?? null,
    data.key_location_note ?? ""
  );
  if (othersNoteErr) {
    return {
      ok: false,
      errors: { key_location_note: othersNoteErr },
      firstMessage: othersNoteErr,
    };
  }

  const passwordErr = validateKeyLocationPasswordForDigital(
    keyLocationCatalog,
    data.key_location_id ?? null,
    data.key_location_password ?? "",
    { allowBlankToKeep: options.allowBlankDigitalPassword }
  );
  if (passwordErr) {
    return {
      ok: false,
      errors: { key_location_password: passwordErr },
      firstMessage: passwordErr,
    };
  }

  return null;
}

export function validatePublicationStep(
  data: DescriptionStepData,
  keyLocationCatalog: KeyLocationCatalogItem[] = [],
  options: KeyLocationStepOptions = {}
): StepValidationResult {
  const keyLocationResult = validateKeyLocationStepFields(
    data,
    keyLocationCatalog,
    options
  );
  if (keyLocationResult) return keyLocationResult;

  const contactErrors = validatePropertyAlternativeContact({
    contact_email: String(data.contact_email ?? ""),
    contact_phone: String(data.contact_phone ?? ""),
  });
  if (Object.keys(contactErrors).length > 0) {
    const firstMessage =
      contactErrors.contact_email ?? contactErrors.contact_phone ?? "";
    return { ok: false, errors: contactErrors, firstMessage };
  }

  try {
    publicationSchema.validateSync(
      {
        ...data,
        title: normalizeRequiredText(data.title),
        description: normalizeRequiredText(data.description),
        owner_email: normalizeRequiredText(data.owner_email),
        property_consultant: normalizeRequiredText(data.property_consultant),
      },
      { abortEarly: false }
    );
    return { ok: true };
  } catch (err) {
    if (err instanceof Yup.ValidationError) return toResult(err);
    throw err;
  }
}

export function validateDescriptionStep(
  data: DescriptionStepData,
  keyLocationCatalog: KeyLocationCatalogItem[] = [],
  options: KeyLocationStepOptions = {}
): StepValidationResult {
  const commissionResult = validateCommissionFields(data);
  if (commissionResult) return commissionResult;

  const keyLocationResult = validateKeyLocationStepFields(
    data,
    keyLocationCatalog,
    options
  );
  if (keyLocationResult) return keyLocationResult;

  try {
    descriptionSchema.validateSync(
      {
        ...data,
        title: normalizeRequiredText(data.title),
        description: normalizeRequiredText(data.description),
        owner_email: normalizeRequiredText(data.owner_email),
        property_consultant: normalizeRequiredText(data.property_consultant),
        zap_publication_type: normalizeRequiredText(data.zap_publication_type),
        rent_value: normalizeMoneyForValidation(data.rent_value),
        condominium_value: normalizeMoneyForValidation(data.condominium_value),
        iptu_value: normalizeMoneyForValidation(data.iptu_value),
        area: normalizeAreaForValidation(data.area),
      },
      { abortEarly: false }
    );
    return { ok: true };
  } catch (err) {
    if (err instanceof Yup.ValidationError) return toResult(err);
    throw err;
  }
}

export function validateCharacteristicsStep(
  data: CharacteristicsStepData
): StepValidationResult {
  try {
    characteristicsSchema.validateSync(data, { abortEarly: false });
    return { ok: true };
  } catch (err) {
    if (err instanceof Yup.ValidationError) return toResult(err);
    throw err;
  }
}

export function validatePhotosForPublish(photoCount: number): StepValidationResult {
  if (photoCount < 1) {
    return {
      ok: false,
      errors: { photos: "Adicione ao menos uma foto para publicar" },
      firstMessage: "Adicione ao menos uma foto para publicar",
    };
  }
  return { ok: true };
}
